/**
 * The engine behind the platform status screens, shared by web and mobile. Each platform supplies an
 * adapter for what differs: how to fetch, how to tell the device is offline, and which lifecycle events
 * mean "look again".
 *
 * A failed request never blocks the app by itself — an ad blocker, a dropped socket or one bad gateway
 * response all look the same. It starts a check, the check is repeated once after a short pause, and
 * only a second failure puts up the "we'll be right back" screen. A device that is offline gets
 * nothing: the problem is not ours to report, so the check stands down and any screen comes off.
 */
import {
  classifyConnectivity,
  clockOffset,
  decideView,
  nextBoundary,
  parseStatusDocument,
  recheckDelay,
  type MaintenanceWindow,
  type PlatformView,
} from "./platformStatusModel";
import { isNonZero } from "@Helpers/numberCondition";

/** The part of a fetch Response the monitor reads; web's and React Native's both satisfy it. */
export interface StatusResponse {
  ok: boolean;
  headers: { get: (name: string) => string | null };
  text: () => Promise<string>;
}

export interface PlatformStatusAdapter {
  /** GETs the status document from the static host. Rejects when nothing answered. */
  fetchStatusDocument: (signal: AbortSignal) => Promise<StatusResponse>;
  /** GETs the backend's health path: true when the app itself answered, false for an infra status. */
  probeBackend: (signal: AbortSignal) => Promise<boolean>;
  /** True only when the platform is certain the device has no connection. */
  isDeviceOffline: () => boolean | Promise<boolean>;
  isForeground: () => boolean;
  /** Calls back when the app returns to the foreground or the device reconnects; returns an unsubscribe. */
  watchLifecycle: (handlers: { onForeground: () => void; onReconnect: () => void }) => () => void;
  /** SHA-256 of the staff bypass token this device holds, where the platform supports one. */
  loadBypassTokenHash?: () => Promise<string | null>;
}

export interface PlatformStatusSnapshot {
  view: PlatformView;
  /** A reconnection check is running (shown on the backend-unreachable screen). */
  checking: boolean;
}

export interface PlatformStatusMonitor {
  /** For useSyncExternalStore: the first subscriber starts the monitor, the last one stops it. */
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => PlatformStatusSnapshot;
  /** Fed by the axios adapter after every finished request. */
  reportBackendReachability: (reachable: boolean) => void;
  /** The "Try again" button. */
  checkNow: () => void;
}

const STATUS_REFRESH_MS = 5 * 60_000;
/** Returning to the foreground refreshes the document if it is older than this. */
const STATUS_STALE_MS = 60_000;
const CONFIRM_DELAY_MS = 3_000;
const REQUEST_TIMEOUT_MS = 10_000;

/** Runs a request with an abort deadline. `AbortSignal.timeout` does not exist on React Native. */
async function withTimeout<T>(run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await run(controller.signal);
  } finally {
    clearTimeout(timer);
  }
}

function parseJson(text: string): unknown {
  try {
    const body: unknown = JSON.parse(text);
    return body;
  } catch {
    return null;
  }
}

export function createPlatformStatusMonitor(adapter: PlatformStatusAdapter): PlatformStatusMonitor {
  let windows: MaintenanceWindow[] = [];
  let bypassSha256: string | null = null;
  let tokenSha256: string | null = null;
  let clockOffsetMs = 0;
  let backendUnreachable = false;
  let checking = false;
  let lastStatusFetch = 0;

  let checkInFlight = false;
  let checkQueued = false;
  let awaitingConfirmation = false;
  let recheckAttempt = 0;
  let recheckTimer: ReturnType<typeof setTimeout> | undefined;
  let boundaryTimer: ReturnType<typeof setTimeout> | undefined;
  let refreshTimer: ReturnType<typeof setTimeout> | undefined;
  let refreshMs = STATUS_REFRESH_MS;
  let running = false;
  let stopWatching: (() => void) | undefined;

  const listeners = new Set<() => void>();
  const serverNow = () => Date.now() + clockOffsetMs;

  const build = (): PlatformStatusSnapshot => ({
    view: decideView({
      windows,
      now: serverNow(),
      bypassed: tokenSha256 !== null && tokenSha256 === bypassSha256,
      backendUnreachable,
    }),
    checking,
  });
  const keyOf = ({ view, checking: busy }: PlatformStatusSnapshot) =>
    JSON.stringify([
      view.screen.kind,
      view.screen.kind === "maintenance" ? view.screen.window : null,
      view.staffBypass,
      busy,
    ]);

  let snapshot = build();
  let snapshotKey = keyOf(snapshot);

  /** Re-evaluates the moment a window starts or ends, so the screen changes on time without polling. */
  function scheduleBoundary() {
    clearTimeout(boundaryTimer);
    const now = serverNow();
    const next = nextBoundary(windows, now);
    if (next !== null) boundaryTimer = setTimeout(publish, next - now + 250);
  }

  /** Recomputes the view; listeners hear about it only when something they render changed. */
  function publish() {
    const next = build();
    const key = keyOf(next);
    if (key !== snapshotKey) {
      snapshot = next;
      snapshotKey = key;
      listeners.forEach((listener) => listener());
    }
    scheduleBoundary();
  }

  async function refreshStatus(): Promise<boolean> {
    lastStatusFetch = Date.now();
    try {
      await withTimeout(async (signal) => {
        const res = await adapter.fetchStatusDocument(signal);
        clockOffsetMs = clockOffset(res.headers.get("date"), res.headers.get("age"), Date.now());
        // A CDN error keeps the last document. A missing document answers with the SPA's index.html
        // (the CDN maps a missing key to it), which means "no maintenance".
        if (!res.ok) return;
        const isJson = (res.headers.get("content-type") ?? "").includes("json");
        const doc = isJson ? parseStatusDocument(parseJson(await res.text())) : null;
        windows = doc?.maintenance ?? [];
        bypassSha256 = doc?.bypassSha256 ?? null;
        const nextRefreshMs = isNonZero(doc?.refreshSeconds) ? doc.refreshSeconds * 1000 : STATUS_REFRESH_MS;
        if (nextRefreshMs !== refreshMs) {
          refreshMs = nextRefreshMs;
          if (running) scheduleRefresh();
        }
      });
      return true;
    } catch {
      return false;
    } finally {
      publish();
    }
  }

  async function probeBackend(): Promise<boolean> {
    try {
      return await withTimeout(adapter.probeBackend);
    } catch {
      return false;
    }
  }

  async function deviceOffline(): Promise<boolean> {
    try {
      return await adapter.isDeviceOffline();
    } catch {
      return false;
    }
  }

  /** Nothing to show: the backend answered, or the device is offline and it is not ours to report. */
  function standDown() {
    clearTimeout(recheckTimer);
    recheckTimer = undefined;
    recheckAttempt = 0;
    awaitingConfirmation = false;
    if (backendUnreachable) {
      backendUnreachable = false;
      publish();
    }
  }

  async function runCheck() {
    if (checkInFlight) {
      checkQueued = true;
      return;
    }
    checkInFlight = true;
    clearTimeout(recheckTimer);
    recheckTimer = undefined;
    if (backendUnreachable) {
      checking = true;
      publish();
    }
    try {
      const offline = await deviceOffline();
      const [backendAnswered, staticHostAnswered] = offline
        ? [false, false]
        : await Promise.all([probeBackend(), refreshStatus()]);
      const result = classifyConnectivity({ deviceOffline: offline, backendAnswered, staticHostAnswered });
      if (result !== "backend-unreachable") {
        standDown();
      } else if (!backendUnreachable && !awaitingConfirmation) {
        awaitingConfirmation = true;
        recheckTimer = setTimeout(() => void runCheck(), CONFIRM_DELAY_MS);
      } else {
        awaitingConfirmation = false;
        backendUnreachable = true;
        recheckTimer = setTimeout(() => void runCheck(), recheckDelay(recheckAttempt));
        recheckAttempt += 1;
      }
    } finally {
      checkInFlight = false;
      checking = false;
      publish();
      if (checkQueued) {
        checkQueued = false;
        void runCheck();
      }
    }
  }

  function reportBackendReachability(reachable: boolean) {
    if (reachable) standDown();
    else if (!backendUnreachable && !awaitingConfirmation && !checkInFlight) void runCheck();
  }

  function checkNow() {
    recheckAttempt = 0;
    void runCheck();
  }

  async function loadBypassToken() {
    try {
      tokenSha256 = (await adapter.loadBypassTokenHash?.()) ?? null;
    } catch {
      tokenSha256 = null;
    }
    publish();
  }

  /** Re-reads the document every `refreshMs` while the app is in the foreground. */
  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      if (adapter.isForeground()) void refreshStatus();
      scheduleRefresh();
    }, refreshMs);
  }

  function start() {
    running = true;
    stopWatching = adapter.watchLifecycle({
      onForeground: () => {
        if (backendUnreachable) checkNow();
        else if (Date.now() - lastStatusFetch > STATUS_STALE_MS) void refreshStatus();
      },
      onReconnect: () => {
        if (!checkInFlight) void runCheck();
      },
    });
    scheduleRefresh();
    void loadBypassToken();
    void refreshStatus();
  }

  function stop() {
    running = false;
    stopWatching?.();
    stopWatching = undefined;
    clearTimeout(refreshTimer);
    clearTimeout(boundaryTimer);
  }

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      if (listeners.size === 1) start();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stop();
      };
    },
    getSnapshot: () => snapshot,
    reportBackendReachability,
    checkNow,
  };
}
